import React from "react"
import { cn } from "@/lib/utils"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import { TrendingUpIcon, TrendingDownIcon, MinusIcon } from "lucide-react"

export interface ValidationMetrics {
  /**
   * Number of interviews conducted
   */
  interviewsConducted: number
  
  /**
   * Target number of interviews (default 10)
   */
  interviewsTarget?: number
  
  /**
   * Number of pain points validated
   */
  painPointsValidated: number
  
  /**
   * Total pain points to test (default 3)
   */
  painPointsTotal?: number

  /**
   * Individual pain point statuses for badge rendering
   */
  painPointStatuses?: ("hypothesis" | "testing" | "validated" | "invalidated")[]

  /**
   * Number of existing alternatives validated
   */
  alternativesValidated: number

  /**
   * Total existing alternatives to test (default 3)
   */
  alternativesTotal?: number

  /**
   * Individual existing alternative statuses for badge rendering
   */
  alternativeStatuses?: ("hypothesis" | "testing" | "validated" | "invalidated")[]

  /**
   * Total number of contacts added
   */
  contactsAdded: number
  
  /**
   * Validation confidence percentage (0-100)
   */
  validationConfidence: number
  
  /**
   * Trend indicators for each metric
   */
  trends?: {
    interviews?: "up" | "down" | "neutral"
    painPoints?: "up" | "down" | "neutral"
    alternatives?: "up" | "down" | "neutral"
    contacts?: "up" | "down" | "neutral"
    confidence?: "up" | "down" | "neutral"
  }
}

export interface ValidationScoreboardProps {
  /**
   * Validation metrics data
   */
  metrics: ValidationMetrics
  
  /**
   * Additional CSS classes
   */
  className?: string
}

const TrendIndicator: React.FC<{ trend?: "up" | "down" | "neutral" }> = ({ trend }) => {
  if (!trend || trend === "neutral") {
    return (
      <MinusIcon className="h-4 w-4 text-muted-foreground" />
    )
  }
  
  if (trend === "up") {
    return (
      <TrendingUpIcon className="h-4 w-4 text-green-600 dark:text-green-500" />
    )
  }
  
  return (
    <TrendingDownIcon className="h-4 w-4 text-red-600 dark:text-red-500" />
  )
}

const getConfidenceColor = (confidence: number) => {
  if (confidence >= 70) {
    return {
      bg: "bg-green-100 dark:bg-green-950",
      text: "text-green-700 dark:text-green-400",
      border: "border-green-200 dark:border-green-800",
      label: "Strong validation"
    }
  }
  
  if (confidence >= 40) {
    return {
      bg: "bg-yellow-100 dark:bg-yellow-950",
      text: "text-yellow-700 dark:text-yellow-400",
      border: "border-yellow-200 dark:border-yellow-800",
      label: "Moderate"
    }
  }
  
  return {
    bg: "bg-red-100 dark:bg-red-950",
    text: "text-red-700 dark:text-red-400",
    border: "border-red-200 dark:border-red-800",
    label: "Needs more data"
  }
}

const getPainPointStatus = (validated: number, total: number) => {
  if (validated === total) {
    return "default"
  }
  if (validated > 0) {
    return "secondary"
  }
  return "outline"
}

export const ValidationScoreboard: React.FC<ValidationScoreboardProps> = ({
  metrics,
  className
}) => {
  const {
    interviewsConducted,
    interviewsTarget = 10,
    painPointsValidated,
    painPointsTotal = 3,
    painPointStatuses = [],
    alternativesValidated,
    alternativesTotal = 3,
    alternativeStatuses = [],
    contactsAdded,
    validationConfidence,
    trends = {}
  } = metrics

  const interviewsProgress = (interviewsConducted / interviewsTarget) * 100
  const confidenceColors = getConfidenceColor(validationConfidence)

  return (
    <div className={cn("w-full", className)}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Interviews Conducted Card */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                Interviews Conducted
              </p>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-foreground">
                  {interviewsConducted}
                </span>
                <span className="text-lg text-muted-foreground">
                  /{interviewsTarget}
                </span>
              </div>
            </div>
            <TrendIndicator trend={trends.interviews} />
          </div>
          
          <div className="space-y-2">
            <Progress value={interviewsProgress} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {Math.round(interviewsProgress)}% of target
            </p>
          </div>
        </div>

        {/* Pain Points Tested Card */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                Pain Points Tested
              </p>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-foreground">
                  {painPointsValidated}
                </span>
                <span className="text-lg text-muted-foreground">
                  /{painPointsTotal}
                </span>
              </div>
            </div>
            <TrendIndicator trend={trends.painPoints} />
          </div>
          
          <div className="flex flex-wrap gap-2">
            {painPointStatuses.length > 0
              ? painPointStatuses.map((status, index) => {
                  const config: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; className?: string }> = {
                    validated: { label: "Validated", variant: "default" },
                    invalidated: { label: "Invalidated", variant: "destructive" },
                    testing: { label: "Testing", variant: "outline", className: "bg-amber-100 text-amber-700 border-amber-300" },
                    hypothesis: { label: "Pending", variant: "outline" },
                  }
                  const c = config[status] || config.hypothesis
                  return (
                    <Badge key={index} variant={c.variant} className={cn("text-xs", c.className)}>
                      {c.label}
                    </Badge>
                  )
                })
              : Array.from({ length: painPointsTotal }).map((_, index) => (
                  <Badge
                    key={index}
                    variant={index < painPointsValidated ? "default" : "outline"}
                    className="text-xs"
                  >
                    {index < painPointsValidated ? "Validated" : "Pending"}
                  </Badge>
                ))
            }
          </div>
        </div>

        {/* Existing Alternatives Tested Card */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                Alternatives Tested
              </p>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-foreground">
                  {alternativesValidated}
                </span>
                <span className="text-lg text-muted-foreground">
                  /{alternativesTotal}
                </span>
              </div>
            </div>
            <TrendIndicator trend={trends.alternatives} />
          </div>

          <div className="flex flex-wrap gap-2">
            {alternativeStatuses.length > 0
              ? alternativeStatuses.map((status, index) => {
                  const config: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; className?: string }> = {
                    validated: { label: "Validated", variant: "default" },
                    invalidated: { label: "Invalidated", variant: "destructive" },
                    testing: { label: "Testing", variant: "outline", className: "bg-amber-100 text-amber-700 border-amber-300" },
                    hypothesis: { label: "Pending", variant: "outline" },
                  }
                  const c = config[status] || config.hypothesis
                  return (
                    <Badge key={index} variant={c.variant} className={cn("text-xs", c.className)}>
                      {c.label}
                    </Badge>
                  )
                })
              : Array.from({ length: alternativesTotal }).map((_, index) => (
                  <Badge
                    key={index}
                    variant={index < alternativesValidated ? "default" : "outline"}
                    className="text-xs"
                  >
                    {index < alternativesValidated ? "Validated" : "Pending"}
                  </Badge>
                ))
            }
          </div>
        </div>

        {/* Contacts Added Card */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                Contacts Added
              </p>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-foreground">
                  {contactsAdded}
                </span>
              </div>
            </div>
            <TrendIndicator trend={trends.contacts} />
          </div>
          
          <div className="pt-2">
            <p className="text-xs text-muted-foreground">
              Total network connections
            </p>
          </div>
        </div>

        {/* Validation Confidence Card */}
        <div className={cn(
          "border rounded-lg p-6 space-y-4",
          confidenceColors.bg,
          confidenceColors.border
        )}>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className={cn("text-sm font-medium", confidenceColors.text)}>
                Validation Confidence
              </p>
              <div className="flex items-baseline gap-1">
                <span className={cn("text-3xl font-bold", confidenceColors.text)}>
                  {validationConfidence}%
                </span>
              </div>
            </div>
            <TrendIndicator trend={trends.confidence} />
          </div>
          
          <div className="pt-2">
            <Badge
              variant="outline"
              className={cn(
                "text-xs font-medium",
                confidenceColors.text,
                confidenceColors.border
              )}
            >
              {confidenceColors.label}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  )
}